import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MenuItem } from '../models/menu-item.model';
import { TranslateModule } from '@ngx-translate/core';
import { filter, Observable, Subject, takeUntil } from 'rxjs';
import { AuthState } from '../../../../shared/store/auth/auth.state';
import { Store } from '@ngxs/store';
import { AdminSideMenuService } from '../../../../shared/services/common/admin-side-menu.service';
import { HelperService } from '../../../../shared/services/common/helper.service';
import { FormService } from '../../../modules/admin-modules/pre-auth/preauth-request/services/form.service';

@Component({
  selector: 'app-admin-panel-sidebar-menu',
  standalone: true,
  imports: [CommonModule, RouterModule, MatExpansionModule, MatIconModule, TranslateModule],
  templateUrl: './admin-panel-sidebar-menu.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminPanelSidebarMenuComponent implements OnInit, AfterViewInit, OnDestroy {
  private renderer = inject(Renderer2);
  private router = inject(Router);
  private store = inject(Store);
  private cdr = inject(ChangeDetectorRef);
  private adminSideMenuService = inject(AdminSideMenuService);
  private helperService = inject(HelperService);
  private formService = inject(FormService);

  authorizedMenuItems: MenuItem[] = [];
  currentUrl: string = '';
  permissions$!: Observable<string[]>;
  onDestroy$: Subject<void> = new Subject<void>();
  isSidebarCollapsed: boolean = true;
  isSidebarHovered: boolean = false;

  constructor() {
    this.permissions$ = this.store.select(AuthState.permissions);
  }

  isPanelExpanded(menu: MenuItem): boolean {
    return menu.subMenus?.some((subMenu) => {
      if (subMenu.subMenus)
        return subMenu.subMenus.some((x) => this.currentUrl.startsWith(x.routerLink[0])) || this.currentUrl.startsWith(subMenu.routerLink[0]);
      return this.currentUrl.startsWith(subMenu.routerLink[0]);
    });
  }

  isActive(menu: MenuItem) {
    const currentUrl = this.router.url;
    const cleanUrl = this.helperService.getUrlWithoutGUID(currentUrl);
    return (menu.routerLink[0] == cleanUrl) || (menu.subPathLinks?.includes(cleanUrl));
  }

  ngOnInit() {
    document.body.classList.add('sidebar-collapsed');
    this.currentUrl = this.router.url;
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd), takeUntil(this.onDestroy$)).subscribe(() => {
      this.currentUrl = this.router.url;
      if (window.innerWidth < 991 && document.body.classList.contains('show-sidebar'))
        document.body.classList.remove('show-sidebar');
    });
    this.formService.collapsed$.pipe(takeUntil(this.onDestroy$)).subscribe(value => this.isSidebarCollapsed = value);
    this.permissions$.pipe(takeUntil(this.onDestroy$)).subscribe(permissions => this.authorizedMenuItems = this.adminSideMenuService.getAuthorizedAdminSideMenu(permissions, true));
    this.router.events.pipe(takeUntil(this.onDestroy$)).subscribe(() => this.cdr.detectChanges());
  }

  ngAfterViewInit(): void {
    this.checkWindowWidth();
  }

  onMouseEnter() {
    const windowWidth = window.innerWidth;
    if (windowWidth > 991 && document.body.classList.contains('sidebar-collapsed')) {
      this.renderer.addClass(document.body, 'sidebar-hovered');
      this.isSidebarCollapsed = false;
    }
  }

  onMouseLeave() {
    this.renderer.removeClass(document.body, 'sidebar-hovered');
    this.isSidebarCollapsed = true;
  }

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.checkWindowWidth();
  }

  private checkWindowWidth(): void {
    const isSmallScreen = window.innerWidth < 991;
    this.renderer.removeClass(document.body, isSmallScreen ? 'sidebar-collapsed' : 'sidebar-full');
    this.renderer.addClass(document.body, isSmallScreen ? 'sidebar-full' : 'sidebar-collapsed');
    this.isSidebarCollapsed = !isSmallScreen;
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    document.body.classList.add('sidebar-collapsed');
    document.body.classList.remove('sidebar-full');
    this.isSidebarCollapsed = true;
  }
}